import * as Sentry from '@sentry/nuxt'
import { SessionExpiredError, redirectToLoginExpired } from './sessionExpired'

const KNOWN: Record<string, string> = {
  'foreign key constraint':         'Referencia inválida. Verifica los datos e intenta de nuevo.',
  'duplicate key value':             'Ya existe un registro con esos datos.',
  'violates unique constraint':      'Ya existe un registro con esos datos.',
  'violates not-null constraint':    'Faltan campos obligatorios.',
  'violates check constraint':       'El valor ingresado no es válido.',
  'permission denied':               'No tienes permiso para realizar esta acción.',
  'new row violates row-level':      'No tienes permiso para realizar esta acción.',
  'jwt expired':                     'Tu sesión expiró. Inicia sesión de nuevo.',
  'invalid input syntax':            'El formato de los datos es incorrecto.',
  'connection refused':              'No se pudo conectar al servidor. Intenta de nuevo.',
  'timeout':                         'La operación tardó demasiado. Intenta de nuevo.',
  'failed to send a request to the edge function': 'No se pudo conectar al servidor. Intenta de nuevo en unos segundos.',
  'failed to fetch':                 'No se pudo conectar al servidor. Verifica tu conexión.',
  'networkerror':                    'No se pudo conectar al servidor. Verifica tu conexión.',
  'network request failed':          'No se pudo conectar al servidor. Verifica tu conexión.',
  'limit reached':                   'Tu clínica alcanzó el límite de su plan actual. Contacta a soporte para ampliarlo.',
}

// Supabase/PostgREST codes that are an expected lifecycle state, NOT a real bug —
// don't flood Sentry with them. PGRST116 = ".single()/.maybeSingle() returned 0
// rows" (was the bulk of the historical "[object Object]" noise). PGRST303 = "JWT
// expired" (the user's session token hit its TTL — the app shows "Tu sesión expiró"
// and they re-login; was ~15 Sentry issues / dozens of events, pure noise).
const SKIP_SENTRY_CODES = new Set(['PGRST116', 'PGRST303'])

// Friendly Spanish names for common DB columns surfaced in errors.
const COLUMN_LABELS: Record<string, string> = {
  full_name: 'nombre',
  first_name: 'nombre',
  last_name: 'apellidos',
  email: 'correo',
  phone: 'teléfono',
  birth_date: 'fecha de nacimiento',
  curp: 'CURP',
  rfc: 'RFC',
  nss: 'NSS',
  doctor_id: 'doctora',
  clinic_id: 'clínica',
  patient_id: 'paciente',
  appointment_id: 'cita',
  consultation_id: 'consulta',
  starts_at: 'fecha y hora',
  language: 'idioma',
  address: 'dirección',
  marital_status: 'estado civil',
  education_level: 'escolaridad',
  emergency_contact_name: 'contacto de emergencia',
  emergency_contact_phone: 'teléfono de emergencia',
}

function labelFor(column: string): string {
  return COLUMN_LABELS[column] ?? column.replace(/_/g, ' ')
}

function extractColumn(rawMsg: string): string | null {
  // PostgreSQL: null value in column "xxx" of relation "..."
  const nullMatch = rawMsg.match(/null value in column "([^"]+)"/i)
  if (nullMatch) return nullMatch[1]
  // unique/check: violates ... constraint "tabla_xxx_key" — extraer xxx
  const constraintMatch = rawMsg.match(/constraint "([^"]+)"/i)
  if (constraintMatch) return constraintMatch[1]
  // Key (xxx)=(...) already exists.
  const keyMatch = rawMsg.match(/Key \(([^)]+)\)=/i)
  if (keyMatch) return keyMatch[1]
  return null
}

function extractRawMessage(e: unknown): string {
  if (e == null) return ''
  if (typeof e === 'string') return e
  if (e instanceof Error) return e.message
  if (typeof e === 'object') {
    const m = (e as { message?: unknown }).message
    if (typeof m === 'string') return m
    if (m != null && typeof m === 'object') {
      const inner = (m as { message?: unknown }).message
      if (typeof inner === 'string') return inner
      try { return JSON.stringify(m) } catch { /* fall through */ }
    }
    try { return JSON.stringify(e) } catch { /* fall through */ }
  }
  return String(e)
}

/**
 * Extrae el mensaje de error de una Edge Function invocada con
 * `supabase.functions.invoke`. supabase-js envuelve el error y deja el body
 * (con `{ error }`) en `error.context` (una Response). Devuelve un Error con
 * el mensaje del body si existe, o el mensaje genérico del wrapper.
 *
 * Uso: `if (error) throw await edgeFunctionError(error)`
 */
export async function edgeFunctionError(error: unknown, response?: Response): Promise<Error> {
  const source = response ?? (error as { context?: Response })?.context
  const body = await source?.json?.().catch(() => null)
  return new Error(body?.error ?? (error as { message?: string })?.message ?? 'Error en la función')
}

export function translateSupabaseError(e: unknown): string {
  // Session died mid-write (the request went out anonymous → 0 rows). Surface an
  // honest message + send to login; do NOT capture in Sentry (expected lifecycle,
  // same as PGRST303). Handled first so it bypasses the generic mapping below.
  if (e instanceof SessionExpiredError || (e as { sessionExpired?: boolean })?.sessionExpired === true) {
    redirectToLoginExpired()
    return (e as Error)?.message || 'Tu sesión expiró. Inicia sesión de nuevo.'
  }

  const rawMsg = extractRawMessage(e)
  const code = (e as { code?: string })?.code
  // Edge fn business validation (deliberate { error: "..." } body, not a system failure).
  // The message is already Spanish and user-readable; skip Sentry to avoid noise.
  if ((e as any)?.isEdgeFnBusinessError) {
    console.error('[supabase-error]', e)
    return rawMsg || 'Error al procesar la solicitud.'
  }
  // Always log so production errors can be diagnosed from the user's console.
  console.error('[supabase-error]', e)
  // supabase-js returns PostgrestError as a PLAIN object (not an Error instance),
  // so `new Error(String(e))` would capture the literal "[object Object]" — the prod
  // bug that grouped 48 events under one unreadable Sentry issue. Build the Error from
  // the real message and tag .name with the code so Sentry groups by code (G-UX-007).
  if (!SKIP_SENTRY_CODES.has(code ?? '')) {
    let captured: Error
    if (e instanceof Error) {
      captured = e
    } else {
      captured = new Error(rawMsg || 'Error de Supabase')
      if (code) captured.name = code
    }
    Sentry.captureException(captured, {
      level: 'error',
      extra: {
        errorCode: code,
        // NOT including Supabase `details`/`hint` — may contain row data (G-UX-007)
      },
    })
  }

  // PostgreSQL RAISE EXCEPTION (code P0001) — surface the message as-is
  // so domain errors like "Stock insuficiente..." reach the user.
  if (code === 'P0001' && rawMsg) return rawMsg

  const raw = rawMsg.toLowerCase()
  const column = extractColumn(rawMsg)

  // NOT NULL — name the missing column.
  if (raw.includes('violates not-null constraint') && column) {
    return `Falta el campo: ${labelFor(column)}.`
  }
  // UNIQUE — name the duplicated column.
  if ((raw.includes('duplicate key value') || raw.includes('violates unique constraint')) && column) {
    return `Ya existe un registro con ese ${labelFor(column)}.`
  }
  // CHECK — name the constraint.
  if (raw.includes('violates check constraint') && column) {
    return `El valor de ${labelFor(column)} no es válido.`
  }

  for (const [key, msg] of Object.entries(KNOWN)) {
    if (raw.includes(key)) return msg
  }

  // Surface postgres details/hint when available so the user can act on it
  // (e.g. "details: phone must be 10 digits").
  const details = (e as { details?: string })?.details
  const hint    = (e as { hint?: string })?.hint
  if (details && details.length < 200) return details
  if (hint && hint.length < 200) return hint
  if (rawMsg && rawMsg.length < 200) return rawMsg

  return 'Ocurrió un error inesperado. Intenta de nuevo.'
}

// Mensajes de GoTrue (auth) → español. Son un set finito y SIEMPRE llegan en
// inglés; por eso, ante uno no mapeado, devolvemos un genérico en español en vez
// del texto crudo (lo que hacía login.vue con `?? e.message`). NO captura en
// Sentry: los fallos de login son esperados y generarían ruido.
const AUTH_KNOWN: Array<[RegExp, string]> = [
  [/invalid login credentials/i,            'Correo o contraseña incorrectos.'],
  [/email not confirmed/i,                  'Tu correo aún no está confirmado. Contacta al administrador.'],
  [/email link is invalid or has expired/i, 'El enlace expiró o no es válido. Solicita uno nuevo.'],
  [/token has expired or is invalid/i,      'El enlace expiró o no es válido. Solicita uno nuevo.'],
  [/user already registered/i,              'Ya existe una cuenta con ese correo.'],
  [/user not found/i,                       'No existe una cuenta con ese correo.'],
  [/new password should be different/i,     'La nueva contraseña debe ser diferente a la anterior.'],
  [/for security purposes/i,                'Por seguridad, espera unos segundos antes de intentar de nuevo.'],
  [/rate limit exceeded|over_.*_rate_limit/i, 'Demasiados intentos. Espera unos minutos e intenta de nuevo.'],

  // ── Cognito (modo AWS) ────────────────────────────────────────────────────
  // Sin estas, una contraseña equivocada caía en el genérico "No se pudo
  // completar la operación": indistinguible de una caída del sistema, así que
  // el usuario no sabía si reintentar o llamar a soporte (staging 2026-08-07).
  //
  // La sesión vencida va PRIMERO: Cognito también la reporta como
  // NotAuthorizedException y si no, se leería como credenciales malas.
  [/refresh token has expired|invalid refresh token/i,
                                            'Tu sesión expiró. Vuelve a iniciar sesión.'],
  // Credenciales malas y usuario inexistente comparten mensaje A PROPÓSITO: son
  // la pantalla de login, y distinguirlos revela qué correos tienen cuenta.
  [/incorrect username or password|notauthorizedexception/i,
                                            'Correo o contraseña incorrectos.'],
  [/user does not exist|usernotfoundexception/i,
                                            'Correo o contraseña incorrectos.'],
  [/attempt limit exceeded|password attempts exceeded|toomanyrequestsexception|limitexceededexception/i,
                                            'Demasiados intentos. Espera unos minutos e intenta de nuevo.'],
  [/usernotconfirmedexception|user is not confirmed/i,
                                            'Tu cuenta aún no está activada. Abre el correo de invitación o pide uno nuevo al administrador.'],
  [/passwordresetrequiredexception/i,       'Debes restablecer tu contraseña. Usa "¿Olvidaste tu contraseña?".'],
  [/invalidpasswordexception/i,             'La contraseña no cumple los requisitos: mínimo 8 caracteres, con mayúscula, minúscula y número.'],
  [/userdisabled|user is disabled/i,        'Tu cuenta está desactivada. Contacta al administrador de la clínica.'],
  [/failed to fetch|networkerror|network request failed|failed to send a request/i,
                                            'No se pudo conectar. Verifica tu conexión e intenta de nuevo.'],
]

/**
 * Traduce errores de Supabase Auth (GoTrue) a español. Úsalo en las páginas de
 * `auth/` en vez de mostrar `e.message` (que llega crudo y en inglés).
 */
export function translateAuthError(e: unknown): string {
  const raw = extractRawMessage(e)

  const pw = raw.match(/password should be at least (\d+) characters/i)
  if (pw) return `La contraseña debe tener al menos ${pw[1]} caracteres.`

  for (const [re, msg] of AUTH_KNOWN) {
    if (re.test(raw)) return msg
  }
  return 'No se pudo completar la operación. Intenta de nuevo.'
}
